import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Collapse,
  Chip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useNavigate } from "react-router-dom";
import { apiData } from "../../services/api_service";
import { returnableItemsAPI, createReturnAPI } from "../../config";
import { formatAED, formatNumber } from "../../utils/format";

const TAX_RATE = 0.05; // UAE VAT

// Return / Exchange against a generated tax invoice.
// - Return: pick items + quantities, mark condition (good/damaged), pick a
//   refund method -> POST /order/returns. Stock reverts to the source store;
//   the Tax Credit Note document is NOT generated (disabled for now).
// - Exchange: same, plus "create replacement invoice" which hands off to the
//   existing Create Invoice screen for the replacement sale.
export default function ReturnExchangeDialog({ open, order, onClose, onDone }) {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [orderInfo, setOrderInfo] = useState(null);
  const [reason, setReason] = useState("");
  const [refundMethod, setRefundMethod] = useState("cash");
  const [createReplacement, setCreateReplacement] = useState(false);

  const orderId = order?.order_id;

  useEffect(() => {
    if (!open || !orderId) return;
    setError("");
    setReason("");
    setRefundMethod("cash");
    setCreateReplacement(false);
    (async () => {
      setLoading(true);
      try {
        const res = await apiData(
          "get",
          `${returnableItemsAPI}/${orderId}/returnable`,
        );
        if (res.success && res.data?.items) {
          setOrderInfo(res.data.order || null);
          setRows(
            res.data.items.map((it) => ({
              order_item_id: it.order_item_id,
              product_id: it.product_id,
              product_name: it.product_name,
              sku: it.sku,
              selling_price: parseFloat(it.selling_price || 0),
              ordered_quantity: Number(it.ordered_quantity || 0),
              returnable_quantity: Number(it.returnable_quantity || 0),
              current_stock: it.is_bundle ? null : Number(it.current_stock || 0),
              is_bundle: !!it.is_bundle,
              components: Array.isArray(it.components) ? it.components : [],
              selected: false,
              quantity: 0,
              condition: "good",
              damaged_reason: "",
              expanded: false,
            })),
          );
        } else {
          setRows([]);
          setError(res.msg || "Could not load returnable items.");
        }
      } catch (e) {
        console.error("Failed to load returnable items:", e);
        setError("Failed to load returnable items.");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, orderId]);

  const updateRow = (idx, patch) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const selectedRows = rows.filter(
    (r) => r.selected && Number(r.quantity) > 0,
  );

  // The invoice applies a header-level discount on the gross line total, so the
  // credit only reverses what the customer actually paid. Mirror the backend:
  // discountFactor = order net subtotal ÷ gross of all order lines; credit each
  // selected line net = gross × factor, VAT on net. Falls back to no discount.
  const { subtotalGross, discount, subtotal, tax, total } = useMemo(() => {
    const grossAll = rows.reduce(
      (acc, r) => acc + Number(r.ordered_quantity || 0) * Number(r.selling_price || 0),
      0,
    );
    const orderNet = Number(orderInfo?.subtotal || 0);
    const factor = grossAll > 0 && orderNet > 0 ? orderNet / grossAll : 1;
    const taxRate =
      orderNet > 0 ? Number(orderInfo?.tax_amount || 0) / orderNet : TAX_RATE;

    const gross = selectedRows.reduce(
      (acc, r) => acc + Number(r.quantity) * Number(r.selling_price || 0),
      0,
    );
    const net = gross * factor;
    const t = net * taxRate;
    return {
      subtotalGross: gross,
      discount: gross - net,
      subtotal: net,
      tax: t,
      total: net + t,
    };
  }, [selectedRows, rows, orderInfo]);

  const validate = () => {
    if (selectedRows.length === 0) {
      setError("Select at least one item and quantity to return.");
      return false;
    }
    for (const r of rows) {
      if (!r.selected) continue;
      if (Number(r.quantity) <= 0) {
        setError(`Enter a quantity for ${r.product_name}.`);
        return false;
      }
      if (Number(r.quantity) > r.returnable_quantity) {
        setError(
          `Cannot return ${r.quantity} of ${r.product_name} — only ${r.returnable_quantity} left.`,
        );
        return false;
      }
      if (r.condition === "damaged" && !r.damaged_reason.trim()) {
        setError(`Enter a damage note for ${r.product_name}.`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    setError("");
    if (!validate()) return;

    const payload = {
      order_id: orderId,
      user_id: user?.emp_id,
      reason,
      refund_method: refundMethod,
      items: selectedRows.map((r) => ({
        order_item_id: r.order_item_id,
        product_id: r.product_id,
        quantity: Number(r.quantity),
        condition: r.condition,
        restock: r.condition === "good",
        damaged_reason: r.condition === "damaged" ? r.damaged_reason : null,
      })),
    };

    try {
      setSubmitting(true);
      const res = await apiData("post", createReturnAPI, payload);
      if (res.success) {
        // Credit note PDF is no longer generated on return/exchange — nothing
        // to open. Stock still reverts to the source store server-side.
        // const pdf = res.data?.pdf_url;
        // if (pdf) window.open(pdf, "_blank", "noopener");
        if (createReplacement) {
          // Exchange: hand off to the tested Create Invoice screen to ring up
          // the replacement sale for this same customer.
          navigate("/invoice/add", {
            state: { exchangeFrom: order, prefillCustomerId: order?.customer_id },
          });
          return;
        }
        onDone?.(res.data);
      } else {
        setError(res.msg || "Failed to process the return.");
      }
    } catch (e) {
      console.error("Return submit failed:", e);
      setError("Something went wrong while processing the return.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        Return / Exchange
        {order ? ` — ${order.order_number}` : ""}
        {orderInfo?.store_name && (
          <Typography variant="body2" color="text.secondary">
            Store: {orderInfo.store_name} · returned stock goes back to this store
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell padding="checkbox" />
                  <TableCell>Product</TableCell>
                  <TableCell>Unit Price</TableCell>
                  <TableCell>Returnable</TableCell>
                  <TableCell>Return Qty</TableCell>
                  <TableCell>Condition</TableCell>
                  <TableCell>Damage Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No returnable items on this invoice.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r, idx) => {
                    const disabled = r.returnable_quantity <= 0;
                    // Only good (restocked) lines change store stock; damaged
                    // returns are written off and leave stock unchanged.
                    const willRestock = r.selected && r.condition === "good";
                    const afterStock =
                      r.current_stock + (willRestock ? Number(r.quantity) : 0);
                    return (
                      <Fragment key={r.order_item_id}>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <IconButton
                            size="small"
                            title="Stock details"
                            onClick={() =>
                              updateRow(idx, { expanded: !r.expanded })
                            }
                          >
                            {r.expanded ? (
                              <KeyboardArrowUpIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowDownIcon fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={r.selected}
                            disabled={disabled}
                            onChange={(e) =>
                              updateRow(idx, {
                                selected: e.target.checked,
                                quantity: e.target.checked
                                  ? r.quantity || r.returnable_quantity
                                  : 0,
                              })
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {r.product_name}
                          <div style={{ fontSize: 11, color: "#666" }}>
                            SKU: {r.sku}
                          </div>
                        </TableCell>
                        <TableCell>{formatAED(r.selling_price)}</TableCell>
                        <TableCell>
                          {r.returnable_quantity} / {r.ordered_quantity}
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            disabled={!r.selected || disabled}
                            value={r.quantity}
                            inputProps={{ min: 0, max: r.returnable_quantity }}
                            onChange={(e) =>
                              updateRow(idx, {
                                quantity: Math.max(
                                  0,
                                  Math.min(
                                    r.returnable_quantity,
                                    parseInt(e.target.value, 10) || 0,
                                  ),
                                ),
                              })
                            }
                            sx={{ width: 90 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            size="small"
                            disabled={!r.selected}
                            value={r.condition}
                            onChange={(e) =>
                              updateRow(idx, { condition: e.target.value })
                            }
                            sx={{ width: 120 }}
                          >
                            <MenuItem value="good">Good</MenuItem>
                            <MenuItem value="damaged">Damaged</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            disabled={!r.selected || r.condition !== "damaged"}
                            value={r.damaged_reason}
                            placeholder={
                              r.condition === "damaged" ? "Reason" : "-"
                            }
                            onChange={(e) =>
                              updateRow(idx, { damaged_reason: e.target.value })
                            }
                          />
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          colSpan={8}
                          sx={{ py: 0, borderBottom: r.expanded ? undefined : "none" }}
                        >
                          <Collapse in={r.expanded} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                my: 1,
                                p: 1.5,
                                backgroundColor: "#f5f8fc",
                                borderRadius: 1,
                              }}
                            >
                              <Box sx={{ mb: r.is_bundle ? 1 : 0 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Store
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {orderInfo?.store_name || "-"}
                                </Typography>
                              </Box>

                              {r.is_bundle ? (
                                <>
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 700, color: "#6a1b9a" }}
                                  >
                                    Bundle — stock is tracked per component
                                    {willRestock && Number(r.quantity) > 0
                                      ? ` (returning ${Number(r.quantity)} bundle${
                                          Number(r.quantity) > 1 ? "s" : ""
                                        })`
                                      : ""}
                                  </Typography>
                                  <Table size="small" sx={{ mt: 0.5 }}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Component</TableCell>
                                        <TableCell>Per Bundle</TableCell>
                                        <TableCell>Current</TableCell>
                                        <TableCell>After Return</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {r.components.map((c) => {
                                        const add = willRestock
                                          ? (c.per_qty || 0) * Number(r.quantity)
                                          : 0;
                                        return (
                                          <TableRow key={c.product_id}>
                                            <TableCell>
                                              {c.product_name}
                                              <div
                                                style={{ fontSize: 11, color: "#666" }}
                                              >
                                                SKU: {c.sku}
                                              </div>
                                            </TableCell>
                                            <TableCell>{formatNumber(c.per_qty)}</TableCell>
                                            <TableCell>{formatNumber(c.current_stock)}</TableCell>
                                            <TableCell
                                              style={{ fontWeight: 700, color: "#1565c0" }}
                                            >
                                              {formatNumber(c.current_stock + add)}
                                              {add > 0 && (
                                                <span
                                                  style={{
                                                    color: "#2e7d32",
                                                    fontWeight: 600,
                                                    marginLeft: 6,
                                                  }}
                                                >
                                                  (+{add})
                                                </span>
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 3,
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                  }}
                                >
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Current Stock
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {formatNumber(r.current_stock)}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ fontSize: 20, color: "#90a4ae" }}>→</Box>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      After Return
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 700, color: "#1565c0" }}
                                    >
                                      {afterStock}
                                      {willRestock && Number(r.quantity) > 0 && (
                                        <span
                                          style={{
                                            color: "#2e7d32",
                                            fontWeight: 600,
                                            marginLeft: 6,
                                          }}
                                        >
                                          (+{Number(r.quantity)})
                                        </span>
                                      )}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}

                              {r.selected && r.condition === "damaged" && (
                                <Chip
                                  size="small"
                                  color="warning"
                                  label="Damaged — added to Damaged stock (not sellable)"
                                  sx={{ mt: 1 }}
                                />
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "flex-start",
              }}
            >
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Refund Method</InputLabel>
                <Select
                  label="Refund Method"
                  value={refundMethod}
                  onChange={(e) => setRefundMethod(e.target.value)}
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="card">Card</MenuItem>
                  <MenuItem value="receivable_adjustment">
                    Reduce Receivable
                  </MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                label="Reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                sx={{ flex: 1, minWidth: 240 }}
              />

              <Box sx={{ textAlign: "right", minWidth: 220 }}>
                <Typography variant="body2">
                  Subtotal: {formatAED(subtotalGross)}
                </Typography>
                {discount > 0.005 && (
                  <Typography variant="body2">
                    Discount Reversed: −{formatAED(discount)}
                  </Typography>
                )}
                <Typography variant="body2">
                  VAT: {formatAED(tax)}
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Total Refund: {formatAED(total)}
                </Typography>
              </Box>
            </Box>

            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  checked={createReplacement}
                  onChange={(e) => setCreateReplacement(e.target.checked)}
                />
              }
              label="Exchange — create a replacement invoice for this return"
            />
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || loading || selectedRows.length === 0}
        >
          {submitting
            ? "Processing..."
            : createReplacement
              ? "Return & Create Replacement"
              : "Process Return"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
